import React, { useEffect, useRef, useState, useContext } from 'react';
import { useParams, useHistory } from "react-router-dom";
import { ServiceRegistry } from '../../models/service-registry';
import { FilterStorage } from '../../models/filterstorage';
import { EventService } from '../../models/event-service';
import { CLOUDIDS, IMS_EVENT, OMEGA_ENV, SUBMENU } from '../../constants';
import moment from "moment-timezone";
import EventFilter from '../eventfilter/eventfilter';
import Filter from '../filter/filter';
import { createBrowserHistory } from 'history';
import { clearPageState, isHistory, isToday, toggle_visibility, HideFooter, isCommunication, getMyEventsPref, setMyEventsPref, MY_EVENTS_PREF_KEY, resolveMyEventsPref } from '../../utils';
import Spinner from '../spinner/spinner';
import { ConfigContext } from "../../App";
import ProductTable from '../producttable/producttable';
import BreadCrumbs from '../breadcrumbs/breadcrumbs';
import { CasCadingFilterProp, EventType, FilterKey, Incident, IncidentGroup } from '../../proptype';
import Banner from '../banner/banner';
import OMEGA from '../../omega';
import GenericError from '../genericerror/genericerror';
import EventTile from '../eventtile/eventtile';
import { SubscriptionService } from '../../models/subscription-service';

export default function Cloud() {
    const eventDefaultValue = {
        element: "Events",
        action: "click",
        type: "List items",
        widget: {
            name: "cloud",
            type: "component"
        }
    }

    function omega_capture(element: any, widget_name: any, widget_type: any) {
        OMEGA.init().updateAndTrack("event", [{
            ...eventDefaultValue,
            element: element,
            widget: {
                name: widget_name,
                type: widget_type
            },
        }]);
    }

    let defaultHistory: any = { historyDates: [], productHistoryData: [] };
    const locales = useContext(ConfigContext).appConfig.languageData;
    const updateMenu = useContext(ConfigContext).updateMenu;

    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [productList, setProductList] = useState([]);
    const [historyProductList, setHistoryProductList] = useState(defaultHistory);
    const [isHistoryData, setIsHistoryData] = useState(false);
    const [eventFilter, setEventFilter] = useState('all');
    const [filterClicked, setFilterClicked] = useState(false);
    const [mounted, setMounted] = useState(true);
    const [filterCount, setFilterCount] = useState(0);
    const [hasUpdate, setHasUpdate] = useState(false);
    const [events, setEvents] = useState<IncidentGroup[]>([]);
    const [subscriptions, setSubscriptions] = useState<any[]>([]);
    const [selfSubscriptions, setSelfSubscriptions] = useState<any[]>([]);
    const [entitlements, setEntitlements] = useState<any[]>([]);
    const [loadingSubscription, setLoadingSubscription] = useState(false);
    const [isMyEvents, setIsMyEvents] = useState(false);
    const [myEventsDataReady, setMyEventsDataReady] = useState(false);
    const [openEvent, setOpenEvent] = useState(false);
    const [filteredEvents, setFilteredEvents] = useState<any[]>([]);
    const [myProductList, setMyProductList] = useState<any[]>([]);
    const [clearAll, setClearAll] = useState(false);
    const histroyAPI = useHistory();
    const [allEvents, setAllEvents] = useState<IncidentGroup[]>([]);

    const CLOUDS: any = {
        'creative_cloud': CLOUDIDS.CREATIVE_CLOUD_ID,
        'experience_cloud': CLOUDIDS.EXPERIENCE_CLOUD_ID,
        'document_cloud': CLOUDIDS.DOCUMENT_CLOUD_ID,
        'cloud_services': CLOUDIDS.ADOBE_SERVICES_ID,
        'experience_platform': CLOUDIDS.ADOBE_CLOUD_PLATFORM_ID,
    };

    const {eventId, cloudName }: any = useParams();

    const serviceRegistry: ServiceRegistry = ServiceRegistry.init();
    const eventService: EventService = EventService.init();
    eventService.setLocales(locales);
    localStorage.removeItem('filterObj');
    const filterObj = FilterStorage.init(CLOUDS[cloudName]);
    const subscriptionService: SubscriptionService = SubscriptionService.init();
    const loadDataRef = useRef<() => void>(() => {});
    loadDataRef.current = loadData;
    const appliedMyEventsDefaultRef = useRef(false);

    const handleClickAllEvents = () => {
        if (window.adobeIMS?.isSignedInUser()) {
            setMyEventsPref(MY_EVENTS_PREF_KEY, 'all');
        }
        setIsMyEvents(false);
        OMEGA.init().updateAndTrack('event', [{
            ...eventDefaultValue,
            element: 'All Events',
            widget: { name: 'cloud events tab', type: 'panel' },
        }]);
    };

    const handleClickMyEvents = () => {
        if (!window.adobeIMS?.isSignedInUser()) {
            sessionStorage.setItem('pendingReturnPath', window.location.pathname);
            window.adobeid.redirect_uri = window.location.href;
            window.adobeIMS.signIn();
            return;
        }
        // Persisting is conditional (below) so an empty My Events selection doesn't carry
        // forward to other cloud pages, but the toggle itself must still switch on for this
        // page - otherwise clicking My Events with zero events looks like the click did nothing.
        const hasEvents = eventService.getMyEvents(subscriptions, selfSubscriptions, entitlements).length > 0;
        setMyEventsPref(MY_EVENTS_PREF_KEY, hasEvents ? 'myevents' : 'all');
        setIsMyEvents(true);
        OMEGA.init().updateAndTrack('event', [{
            ...eventDefaultValue,
            element: 'My Events',
            widget: { name: 'cloud events tab', type: 'panel' },
        }]);
    };

    function applyCloudEventsTabPreference() {
        appliedMyEventsDefaultRef.current = false;
        setIsMyEvents(resolveMyEventsPref());
    }

    function isMyEventsViewActive() {
        return window.adobeIMS?.isSignedInUser() && myEventsDataReady && isMyEvents;
    }

    function getMyProductIds() {
        return eventService.getMyProductIdsForCloud(
            CLOUDS[cloudName],
            selfSubscriptions,
            entitlements,
            (productId: string) => serviceRegistry.getCloudId(productId),
            subscriptions
        );
    }

    function filterProductsForMyEvents(productList: any[]) {
        if (!isMyEventsViewActive()) {
            return productList;
        }
        const myProductIds = getMyProductIds();
        return productList.map((product: any) =>
            myProductIds.has(String(product.id)) ? product : {
                ...product,
                events: undefined,
                incidents: [],
                formattedMaintenanceDate: undefined,
            }
        );
    }

    function filterHistoryProductsForMyEvents(historyData: any) {
        if (!isMyEventsViewActive()) {
            return historyData;
        }
        const filteredProducts = filterProductsForMyEvents(historyData.productHistoryData || []);
        return {
            ...historyData,
            productHistoryData: filteredProducts,
        };
    }

    function isWaitingForMyEventsData() {
        return window.adobeIMS?.isSignedInUser()
            && isMyEvents
            && !myEventsDataReady;
    }

    useEffect(() => {
        applyCloudEventsTabPreference();
    }, [cloudName]);

    useEffect(() => {
        if (!myEventsDataReady || appliedMyEventsDefaultRef.current) {
            return;
        }
        appliedMyEventsDefaultRef.current = true;
        if (!isMyEvents || !window.adobeIMS?.isSignedInUser()) {
            return;
        }
        const hasAnyMyEvents = eventService.getMyEvents(subscriptions, selfSubscriptions, entitlements).length > 0;
        if (!hasAnyMyEvents) {
            // Nothing to show in My Events by default - fall back to All Events silently.
            // Only applies to the initial/default resolution, never to an explicit manual toggle.
            setIsMyEvents(false);
        }
    }, [myEventsDataReady, isMyEvents]);

    useEffect(() => {
        if (mounted) {
             setClearAll(true);
            filterObj.setContext({
                type: 'cloud',
                cloud: CLOUDS[cloudName]
            });
            updateCurrentMenu();
            OMEGA.init().updateAndTrack('page', {
                hierarchy: "home:cloud",
                name: getCloudName(),
                url: encodeURIComponent(window.location.href),
                type: "home",
                env: OMEGA_ENV
            });

            loadData();
        }

    }, [cloudName]);

    useEffect(() => {
        if (hasUpdate) {
            onFilterChange();
            setHasUpdate(false);
        }
    }, [hasUpdate]);

    useEffect(() => {
        if (myEventsDataReady) {
            onFilterChange();
        }
        // onFilterChange is intentionally omitted: it's a plain (non-memoized) function
        // recreated every render and calls setProductList/setHistoryProductList/etc.,
        // so adding it here would re-run this effect after every render it triggers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMyEvents, myEventsDataReady, selfSubscriptions, entitlements]);

    useEffect(() => {
       
        if (openEvent && !loading && typeof eventId === 'string') {

            const _element = document.querySelector(`.id-${eventId}`);
            if (_element) {
                _element.classList.add('active');
                setTimeout(() => {
                    
                    _element.scrollIntoView({
                        behavior: 'smooth'
                    });
                }, 200)
                setOpenEvent(false);
               
            }

        }
    }, [openEvent, loading]);

    useEffect(() => {
        updateCurrentMenu();
        OMEGA.init().updateAndTrack('page', {
            hierarchy: "home:cloud",
            name: getCloudName(),
            url: encodeURIComponent(window.location.href),
            type: "home",
            env: OMEGA_ENV
        });
        eventService.registerForUpdate(() => {
            setHasUpdate(true);
        })

        applyCloudEventsTabPreference();

        const [servicePromise, productPromise]: any = [serviceRegistry.fetch(), eventService.fetch()];
        filterObj.setContext({
            type: 'cloud',
            cloud: CLOUDS[cloudName]
        });
        window.scrollTo(0, 0);
        let state: any = histroyAPI.location.state;
        let safe: boolean = !!state?.page;
        if (safe) {
            clearPageState(createBrowserHistory());
        }
        if (window.adobeIMS?.isSignedInUser()) {
            setLoadingSubscription(true);
            subscriptionService.clearMyEventsContextCache();
        }
        const myEventsPromise = window.adobeIMS?.isSignedInUser()
            ? subscriptionService.fetchMyEventsContext(serviceRegistry, safe)
            : Promise.resolve(null);

        Promise.all([servicePromise, productPromise, myEventsPromise])
            .then((results) => {
                if (mounted) {
                    const myEventsContext = results[2];
                    if (myEventsContext) {
                        setSubscriptions(myEventsContext.eventSubscription);
                        setSelfSubscriptions(myEventsContext.selfSubscriptions);
                        setEntitlements(myEventsContext.entitlements);
                        setMyEventsDataReady(true);
                        setLoadingSubscription(false);
                    } else {
                        setMyEventsDataReady(true);
                    }
                    updateCurrentMenu();
                    loadData();
                    setLoading(false);
                }
            })

            .catch((error: any) => {
                console.error('Error in cloud service promises:', error);
                omega_capture(
                    `Error in cloud service promises: ${error.message || error.toString()}`,
                    "cloud",
                    "error"
                );
                setLoading(false);
                setError(true);
            });

        const handleImsReady = () => {
            applyCloudEventsTabPreference();
            if (!window.adobeIMS?.isSignedInUser()) {
                setMyEventsDataReady(true);
                return;
            }
            const currentSafe = !!(histroyAPI.location.state as any)?.page;
            subscriptionService.clearMyEventsContextCache();
            setLoadingSubscription(true);
            setLoading(true);
            subscriptionService.fetchMyEventsContext(serviceRegistry, currentSafe)
                .then((context: any) => {
                    if (mounted && context) {
                        setSubscriptions(context.eventSubscription);
                        setSelfSubscriptions(context.selfSubscriptions);
                        setEntitlements(context.entitlements);
                    }
                    if (mounted) {
                        setMyEventsDataReady(true);
                        setLoadingSubscription(false);
                        loadDataRef.current();
                        setLoading(false);
                    }
                })
                .catch(() => {
                    if (mounted) {
                        setMyEventsDataReady(true);
                        setLoadingSubscription(false);
                        loadDataRef.current();
                        setLoading(false);
                    }
                });
        };

        if (!window.adobeIMS) {
            document.addEventListener(IMS_EVENT, handleImsReady);
        }

        return () => {
            setMounted(false);
            document.removeEventListener(IMS_EVENT, handleImsReady);
        }

    }, []);

    function loadData() {
        if (CLOUDS[cloudName]) {
            let filter = filterObj.getValue(),
                date = moment().format('YYYY-MM-DD'),
                history: boolean = false;

            if (filter?.date && filter.date.length === 2) {
                history = isHistory([moment(filter?.date[0]), moment(filter?.date[1])]);
            } else {
                filterObj.setValue('date', [date, date])
            }
            setIsHistoryData(history);
            var filterList: any = [];
            if (history) {
                filterList= eventService.filterProductsByDate(serviceRegistry, filterObj.getValue(), CLOUDS[cloudName], undefined, isMyEventsViewActive() ? getMyProductIds() : undefined);
                setHistoryProductList(filterList);
            } else {
                filterList= eventService.filterProducts(serviceRegistry, filterObj.getValue(), CLOUDS[cloudName], undefined);
                setProductList(filterList);
            }
            setFilterCount(getFilterCount());
             if (eventId) {
                            
            let incident = eventService.getIncident(eventId);
            if (incident) {  
                setOpenEvent(true);
            }
            
        }
        }

        const onGoingEvents: IncidentGroup[] = eventService.getOnGoingEventsByType(CLOUDS[cloudName], undefined, undefined, undefined, undefined, undefined);
        setEvents(onGoingEvents);
        
        // For the event ID dropdown, use getIncidentsByDate to respect date filters
        const filter = filterObj.getValue();
    
        
        // Ensure we have a valid filter with at least empty arrays for products
        const dateFilter = {
            products: [], // We don't filter by products for cloud page
            date: filter?.date && filter.date.length === 2 ? 
                [filter.date[0], filter.date[1]] : 
                [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
            offerings: filter?.offerings || [],
            regions: filter?.regions || [],
            environments: filter?.environments || []
        };
        
        const allIncidents = eventService.getIncidentsByDate(dateFilter, CLOUDS[cloudName], 'all', {value: 'all', label: locales.all_status} ,undefined,undefined,true);
        
        setAllEvents(allIncidents);
        
    }


    function setBannerEvents(filterList: any, history: boolean) {
        var bannerList: any[] = [];
        if(history){

            filterList.productHistoryData.forEach((productData: any) => {
                if(productData.incidents.length>0){
                    // eventType is what getOnGoingEventsByType()'s classification reads.
                    bannerList = [...bannerList, ...productData.incidents.map((incident: any) => ({
                        ...incident,
                        eventType: eventService.getEventTypeForProductIncident(incident),
                    }))];
                }
            })
        }
        else{
            filterList.forEach((item: any) => {
                if(item.incidents.length>0){
                    // eventType is what getOnGoingEventsByType()'s classification reads.
                    bannerList = [...bannerList, ...item.incidents.map((incident: any) => ({
                        ...incident,
                        eventType: eventService.getEventTypeForProductIncident(incident),
                    }))];
                }
            })
        }
        setFilteredEvents(bannerList);


    }

    useEffect(() => {
        if (isHistoryData) {
            setBannerEvents(filterHistoryProductsForMyEvents(historyProductList), true);
            return;
        }
        if (isMyEventsViewActive()) {
            // Recompute (rather than mask productList) so each product's own incident count is
            // narrowed to just what I'm subscribed to, instead of showing its full unfiltered count.
            const myEventsMatcher = eventService.buildMyEventsMatcher(subscriptions, selfSubscriptions, entitlements);
            const narrowedList = eventService.filterProducts(serviceRegistry, filterObj.getValue(), CLOUDS[cloudName], undefined, myEventsMatcher);
            setMyProductList(narrowedList);
            setBannerEvents(narrowedList, false);
        } else {
            setMyProductList(productList);
            setBannerEvents(productList, false);
        }
    }, [isMyEvents, myEventsDataReady, productList, historyProductList, isHistoryData, subscriptions, selfSubscriptions, entitlements]);
    function updateCurrentMenu() {
        let currentMenu = getCloudName();
        if (!currentMenu?.length) {
            histroyAPI.push('/');
            return;
        }
        updateMenu(currentMenu);
    }
    function getFilterCount(_eventFilter?: string) {

        filterObj.setContext({
            type: 'cloud',
            cloud: CLOUDS[cloudName]
        });

        let filter: CasCadingFilterProp | undefined = filterObj.getValue(), count = 0;
        if (filter) {
            Object.keys(filter).forEach((key: string) => {
                if (filter && filter[key as keyof CasCadingFilterProp]?.length) {
                    count++;
                }

                if (key === FilterKey.Date && filter?.date?.length) {
                    if (isToday(moment(filter.date[0])) && isToday(moment(filter.date[1]))) {
                        count--;
                    }
                }
            });
        }
        if (_eventFilter) {
            count += _eventFilter !== 'all' ? 1 : 0;
        } else {
            count += eventFilter !== 'all' ? 1 : 0;
        }
        return count;
    }

    function onFilterChange(_eventType?: any) {
        let filter = filterObj.getValue(),
            _mStart = filter?.date && filter?.date[0] ? moment(filter?.date[0]) : moment(),
            _mEnd = filter?.date && filter?.date[1] ? moment(filter?.date[1]) : moment();

            
        
        // Store the current eventIds filter
        const currentEventIds = filter?.eventIds;
        
        // When date range changes, we need to update all events for the dropdown
        const tempFilter = { ...filter };
        if (tempFilter.eventIds) {
            delete tempFilter.eventIds; // Remove event IDs to get all events for the dropdown
        }
        
        // Ensure we have a valid filter with at least empty arrays for products
        const dateFilter = {
            products: [], // We don't filter by products for cloud page  
            date: [_mStart.format('YYYY-MM-DD'), _mEnd.format('YYYY-MM-DD')],
            offerings: tempFilter?.offerings || [],
            regions: tempFilter?.regions || [],
            environments: tempFilter?.environments || []
        };
        
        // Get all events for the date/filter using getIncidentsByDate to respect date filters
        const allIncidents = eventService.getIncidentsByDate(dateFilter, CLOUDS[cloudName], 'all', {value: 'all', label: locales.all_status} ,undefined,undefined,true);
        
        
        setAllEvents(allIncidents);
        
        // Add back event IDs filter if it existed
        if (currentEventIds && currentEventIds.length) {
            filterObj.setValue('eventIds', currentEventIds);
        }
        
        // Update product lists based on filters
        let filterList: any = [];
        if (isHistory([_mStart, _mEnd])) {
            setIsHistoryData(true);
            filterList = eventService.filterProductsByDate(serviceRegistry, filterObj.getValue(), CLOUDS[cloudName], undefined, isMyEventsViewActive() ? getMyProductIds() : undefined);
            setHistoryProductList(filterList);

        } else {
            setIsHistoryData(false);
            filterList= eventService.filterProducts(serviceRegistry, filterObj.getValue(), CLOUDS[cloudName], undefined);
            setProductList(filterList);
        }
        

        // Update displayed events based on the eventIds filter - still using getOnGoingEventsByType for display
        if (filter?.eventIds && filter.eventIds.length) {
            const filteredOnGoingEvents = eventService.getOnGoingEventsByType(CLOUDS[cloudName], undefined, filter.eventIds, undefined, undefined, undefined);
            setEvents(filteredOnGoingEvents);
        } else {
            // If no event IDs filter, show all events from current date
            const onGoingEvents = eventService.getOnGoingEventsByType(CLOUDS[cloudName], undefined, undefined, undefined, undefined, undefined);
            setEvents(onGoingEvents);
        }
        

        setFilterCount(getFilterCount(_eventType));
    }
    function getCloudName() {
        let menuFiltered = SUBMENU.filter((menu: any) => menu.id === CLOUDS[cloudName]);
        if (menuFiltered.length) {
            return menuFiltered[0].name
        }
        return '';
    }

    function isSubscribed(incident: Incident) {
         if (subscriptions && subscriptions.length > 0) {
           if (isCommunication(incident.eventType)){
            return (subscriptions.length > 0 && subscriptions.filter((subscription: any) => subscription.eventId === incident.incidentId && ( subscription.products.length > 0? subscription.products.includes(incident.productId) : (subscription.clouds.length > 0 && subscription.clouds.includes(incident.cloudName) ))).length > 0);
           }
           else if (incident.eventType !== 'maintenance') {
             return (subscriptions.length > 0 && subscriptions.filter((subscription: any) => subscription.eventId === incident.incidentId && subscription.products.includes(incident.productId)).length > 0);
           } else {
             return (subscriptions.length > 0 && subscriptions.filter((subscription: any) => subscription.eventId === incident.incidentId).length > 0);
           }
         }
       }

    function renderCommunications(_eventType?: any, productList?: any){
        if (_eventType === "announcement" || _eventType === "all"){
        return events.map((event_type: any, index1: any) => {
            return (
              <div key={index1}>
                {event_type.list.map((item: any, index: any) => {
                    {
                        if (isCommunication(item.eventType) && filterProduct(item, productList) ){
                        return (
                            <div key={index}>
                              {
                                <EventTile event={item} key={index} isHistory={true} isSubscribed={isSubscribed(item)} subscriptionLoading={loadingSubscription}
                                />
                              }
                            </div>
                          );
                    }}
                  
                })}
              </div>
            );
          });
        }
    }

    function isCommunication(eventType: string): Boolean {
        if ([EventType.cloud.toString(), EventType.global.toString(), EventType.product.toString()].includes(eventType)){
            return true;
        }        
        return false;
      }

    function filterProduct(item:any, productList?: any): Boolean {

        if (item.eventType == EventType.product.toString() ){
            for (const product_details of productList) {
                if (product_details.id == item.productId){
                    return true;
                }
            }     
            return false;
        }
       return true;
      }
      
    function renderProductTable() {
        if (error) {
            return (
                <GenericError />
            );
        } else {
            return (
                loading || isWaitingForMyEventsData() ? <>{HideFooter(true)}<Spinner /></> :
                    <>{HideFooter(false)}
                    <div className="row product-main">
                    
                        <div className="col-md-3 side-panel">
                            <div id="side-panel">
                                <Filter product={false} type='cloud' cloud={CLOUDS[cloudName]} cloudName={cloudName} onFilter={onFilterChange} clearEventFilter={setEventFilter} events={allEvents} />
                            </div>
                        </div>
                        <div className="col-md-9 cloud-table">
                        <div className="product-tab-content product-list-container">{renderCommunications(eventFilter, isHistoryData ? filterHistoryProductsForMyEvents(historyProductList).productHistoryData : (isMyEventsViewActive() ? myProductList : productList))}</div>
                            <ProductTable
                                eventFilter={eventFilter}
                                isHistory={isHistoryData}
                                productList={isHistoryData ? filterHistoryProductsForMyEvents(historyProductList) : (isMyEventsViewActive() ? myProductList : productList)}
                                page={"products"}
                                filter={filterObj.getValue()}
                                showMyEventsToggle={true}
                                isMyEvents={isMyEvents}
                                preferMyEvents={isMyEvents}
                                onAllClick={handleClickAllEvents}
                                onMyEventsClick={handleClickMyEvents}
                            />
                        </div>
                    </div></>
            )
        }
    }

    return (
        <div className="cloud-container">
            <Banner isParentLoading={loading} id={CLOUDS[cloudName]} showOnlyScheduledCanceledMaintenace={true} isMyEventsSource={isMyEventsViewActive() && !isHistoryData} cloudEvents={filteredEvents}/>
            <div className="container cloud-container">
                <nav aria-label="breadcrumb">
                    <BreadCrumbs cloudName={getCloudName()} isLoading={loading} />
                </nav>
                <h1 className="cloud-name title">{getCloudName()}</h1>
                <div className="product-title">
                    <span >{locales.products}</span>
                </div>
                <div id="filter-button">
                    <button className="btn" onClick={() => { setFilterClicked(!filterClicked); toggle_visibility("side-panel", "event-filter") }}>
                        <i className={filterClicked || filterCount ? "event-icon filter active" : "event-icon filter"}></i> {filterCount ? <span>({filterCount})</span> : ''}
                    </button>
                </div>
                <div className="product-tab-content">
                    <div id="event-filter">
                        <EventFilter active={eventFilter}
                            onEventFilter={(eventValue: string) => {

                                OMEGA.init().updateAndTrack("event", [{
                                    ...eventDefaultValue,
                                    feature: cloudName + "-" + "cloud page" + " filters",
                                    widget: {
                                        name: "event_type_filter",
                                        type: "panel"
                                    },
                                    search: {
                                        term: eventValue
                                    }
                                }]);

                                setFilterCount(getFilterCount(eventValue));
                                setEventFilter(eventValue);
                            }}
                        />
                    </div>
                    {renderProductTable()}
                </div>

            </div>
        </div>

    )
}
